import {
  ActiveAtomicTaskUpdate,
  ActiveObjectiveUpdate,
  ChecklistExemptionUpdate,
  ChecklistUpdate,
  InterventionUpdate,
  JobConfigUpdate,
  JobServiceSubscriptionMessage,
  JobUpdate,
  ObjectiveAssignmentUpdate,
  ObjectiveUpdate,
  JobServiceSubscriptionMessage_Type as SubscriptionMessageType,
  JobServiceSubscriptionRequest_Target as SubscriptionRequestTarget,
  TaskUpdate,
} from "protos/rtc/web_socket";
import { useCallback, useRef, useState } from "react";
import { useJobWebsocket } from "./useJobWebsocket";

export const useLiveRobotJobData = ({
  serial,
  onActiveAtomicTaskUpdate,
  onActiveObjectiveUpdate,
  onAssignmentUpdate,
  onChecklistUpdate,
  onChecklistExemptionUpdate,
  skip = false,
}: {
  serial: string;
  onActiveAtomicTaskUpdate?: (resp: ActiveAtomicTaskUpdate) => void;
  onActiveObjectiveUpdate?: (resp: ActiveObjectiveUpdate) => void;
  onAssignmentUpdate?: (resp: ObjectiveAssignmentUpdate) => void;
  onChecklistUpdate?: (resp: ChecklistUpdate) => void;
  onChecklistExemptionUpdate?: (resp: ChecklistExemptionUpdate) => void;
  skip?: boolean;
}): void => {
  const [needReSubscription, setNeedReSubscription] = useState<boolean>(false);

  const onJobUpdate = useCallback(
    (m: JobServiceSubscriptionMessage) => {
      // when there is no active objective/task, the service sends us a
      // subscription update where the objective/task is an empty object, which
      // gets unmarshaled as an objective/task where all fields are 0; flatten
      // those to undefined.
      if (m.activeAtomicTask) {
        if (m.activeAtomicTask.task && !m.activeAtomicTask.task.id) {
          m.activeAtomicTask.task = undefined;
        }
        onActiveAtomicTaskUpdate?.(m.activeAtomicTask);
      }
      if (m.activeObjective) {
        if (m.activeObjective.objective && !m.activeObjective.objective.id) {
          m.activeObjective.objective = undefined;
        }
        onActiveObjectiveUpdate?.(m.activeObjective);
      }
      if (m.objectiveAssignment) {
        onAssignmentUpdate?.(m.objectiveAssignment);
      }
      if (m.checklist) {
        onChecklistUpdate?.(m.checklist);
      }
      if (m.checklistExemption) {
        onChecklistExemptionUpdate?.(m.checklistExemption);
      }
    },
    [
      onActiveAtomicTaskUpdate,
      onActiveObjectiveUpdate,
      onAssignmentUpdate,
      onChecklistUpdate,
      onChecklistExemptionUpdate,
    ]
  );
  const { authenticated, subscribe, unsubscribe } = useJobWebsocket();

  const lastId = useRef<string | undefined>();
  const id = serial;
  const target = SubscriptionRequestTarget.ROBOT;
  const types = [
    SubscriptionMessageType.ACTIVE_ATOMIC_TASK,
    SubscriptionMessageType.ACTIVE_OBJECTIVE,
    SubscriptionMessageType.OBJECTIVE_ASSIGNMENT,
    SubscriptionMessageType.CHECKLIST,
    SubscriptionMessageType.CHECKLIST_EXEMPTION,
  ];
  if (!skip && authenticated && (lastId.current !== id || needReSubscription)) {
    handleSubUnSub(
      id,
      lastId.current,
      target,
      types,
      subscribe,
      unsubscribe,
      onJobUpdate
    );
    setNeedReSubscription(false);
    lastId.current = id;
  }
  const [activeAtomicTaskCallback, setActiveAtomicTaskCallback] = useState<
    ((a: ActiveAtomicTaskUpdate) => void) | undefined
  >(() => onActiveAtomicTaskUpdate);
  const [activeObjectiveCallback, setActiveObjectiveCallback] = useState<
    ((a: ActiveObjectiveUpdate) => void) | undefined
  >(() => onActiveObjectiveUpdate);
  const [assignmentCallback, setAssignmentCallback] = useState<
    ((a: ObjectiveAssignmentUpdate) => void) | undefined
  >(() => onAssignmentUpdate);
  const [checklistCallback, setChecklistCallback] = useState<
    ((a: ObjectiveAssignmentUpdate) => void) | undefined
  >(() => onChecklistUpdate);
  const [checklistExemptionCallback, setChecklistExemptionCallback] = useState<
    ((a: ObjectiveAssignmentUpdate) => void) | undefined
  >(() => onChecklistExemptionUpdate);

  if (!authenticated && !needReSubscription) {
    setNeedReSubscription(true);
  }

  if (
    activeAtomicTaskCallback !== onActiveAtomicTaskUpdate ||
    activeObjectiveCallback !== onActiveObjectiveUpdate ||
    assignmentCallback !== onAssignmentUpdate ||
    checklistCallback !== onChecklistUpdate ||
    checklistExemptionCallback !== onChecklistExemptionUpdate
  ) {
    setNeedReSubscription(true);
    setActiveAtomicTaskCallback(() => onActiveAtomicTaskUpdate);
    setActiveObjectiveCallback(() => onActiveObjectiveUpdate);
    setAssignmentCallback(() => onAssignmentUpdate);
    setChecklistCallback(() => onChecklistUpdate);
    setChecklistExemptionCallback(() => onChecklistExemptionUpdate);
  }
};

export const useInterventionByRobotSubscription = ({
  serial,
  onInterventionUpdate,
  skip = false,
}: {
  serial: string;
  onInterventionUpdate?: (resp: InterventionUpdate) => void;
  skip?: boolean;
}): void => {
  const [needReSubscription, setNeedReSubscription] = useState<boolean>(false);
  const onJobUpdate = useCallback(
    (m: JobServiceSubscriptionMessage) => {
      if (m.intervention) {
        onInterventionUpdate?.(m.intervention);
      }
    },
    [onInterventionUpdate]
  );
  const { authenticated, subscribe, unsubscribe } = useJobWebsocket();

  const lastId = useRef<string | undefined>();
  const id = serial;
  const target = SubscriptionRequestTarget.ROBOT;
  const types = [SubscriptionMessageType.INTERVENTION];
  if (!skip && authenticated && (lastId.current !== id || needReSubscription)) {
    handleSubUnSub(
      id,
      lastId.current,
      target,
      types,
      subscribe,
      unsubscribe,
      onJobUpdate
    );
    setNeedReSubscription(false);
    lastId.current = id;
  }

  const [interventionCallback, setInterventionCallback] = useState<
    ((u: InterventionUpdate) => void) | undefined
  >(() => onInterventionUpdate);
  if (!authenticated && !needReSubscription) {
    setNeedReSubscription(true);
  }
  if (interventionCallback !== onInterventionUpdate) {
    setNeedReSubscription(true);
    setInterventionCallback(() => onInterventionUpdate);
  }
};

export const useJobSubscription = ({
  jobId,
  onJobUpdate,
  onObjectiveUpdate,
  skip = false,
}: {
  jobId: string;
  onJobUpdate?: (resp: JobUpdate) => void;
  onObjectiveUpdate?: (resp: ObjectiveUpdate) => void;
  skip?: boolean;
}): void => {
  const [needReSubscription, setNeedReSubscription] = useState<boolean>(false);

  const callbackFn = useCallback(
    (m: JobServiceSubscriptionMessage) => {
      if (m.job) {
        onJobUpdate?.(m.job);
      }
      if (m.objective) {
        onObjectiveUpdate?.(m.objective);
      }
    },
    [onJobUpdate, onObjectiveUpdate]
  );
  const { authenticated, subscribe, unsubscribe } = useJobWebsocket();

  const lastId = useRef<string | undefined>();
  const id = jobId;
  const target = SubscriptionRequestTarget.JOB;
  const types = [
    SubscriptionMessageType.JOB,
    SubscriptionMessageType.OBJECTIVE,
  ];
  if (!skip && authenticated && (lastId.current !== id || needReSubscription)) {
    handleSubUnSub(
      id,
      lastId.current,
      target,
      types,
      subscribe,
      unsubscribe,
      callbackFn
    );
    setNeedReSubscription(false);
    lastId.current = id;
  }

  const [objectiveCallback, setObjectiveCallback] = useState<
    ((u: ObjectiveUpdate) => void) | undefined
  >(() => onObjectiveUpdate);
  const [jobCallback, setJobCallback] = useState<
    ((u: JobUpdate) => void) | undefined
  >(() => onJobUpdate);
  if (!authenticated && !needReSubscription) {
    setNeedReSubscription(true);
  }
  if (objectiveCallback !== onObjectiveUpdate || jobCallback !== onJobUpdate) {
    setNeedReSubscription(true);
    setObjectiveCallback(() => onObjectiveUpdate);
    setJobCallback(() => onJobUpdate);
  }
};

export const useJobConfigSubscription = ({
  jobId,
  onJobConfigUpdate,
  skip = false,
}: {
  jobId: string;
  onJobConfigUpdate?: (resp: JobConfigUpdate) => void;
  skip?: boolean;
}): void => {
  const [needReSubscription, setNeedReSubscription] = useState<boolean>(false);
  const callbackFn = useCallback(
    (m: JobServiceSubscriptionMessage) => {
      if (m.jobConfig) {
        onJobConfigUpdate?.(m.jobConfig);
      }
    },
    [onJobConfigUpdate]
  );
  const { authenticated, subscribe, unsubscribe } = useJobWebsocket();

  const lastId = useRef<string | undefined>();
  const id = jobId;
  const target = SubscriptionRequestTarget.JOB_CONFIG;
  const types = [SubscriptionMessageType.JOB_CONFIG];
  if (!skip && authenticated && (lastId.current !== id || needReSubscription)) {
    handleSubUnSub(
      id,
      lastId.current,
      target,
      types,
      subscribe,
      unsubscribe,
      callbackFn
    );
    setNeedReSubscription(false);
    lastId.current = id;
  }

  const [configCallback, setConfigCallback] = useState<
    ((u: JobConfigUpdate) => void) | undefined
  >(() => onJobConfigUpdate);
  if (!authenticated && !needReSubscription) {
    setNeedReSubscription(true);
  }
  if (configCallback !== onJobConfigUpdate) {
    setNeedReSubscription(true);
    setConfigCallback(() => onJobConfigUpdate);
  }
};

export const useObjectiveSubscription = ({
  objectiveId,
  onObjectiveUpdate,
  onTaskUpdate,
  onAssignmentUpdate,
  skip = false,
}: {
  objectiveId: string;
  onObjectiveUpdate?: (resp: ObjectiveUpdate) => void;
  onTaskUpdate?: (resp: TaskUpdate) => void;
  onAssignmentUpdate?: (resp: ObjectiveAssignmentUpdate) => void;
  skip?: boolean;
}): void => {
  const [needReSubscription, setNeedReSubscription] = useState<boolean>(false);
  const onJobUpdate = useCallback(
    (m: JobServiceSubscriptionMessage) => {
      if (m.task) {
        onTaskUpdate?.(m.task);
      }
      if (m.objective) {
        onObjectiveUpdate?.(m.objective);
      }
      if (m.objectiveAssignment) {
        onAssignmentUpdate?.(m.objectiveAssignment);
      }
    },
    [onTaskUpdate, onAssignmentUpdate, onObjectiveUpdate]
  );
  const { authenticated, subscribe, unsubscribe } = useJobWebsocket();
  const lastId = useRef<string | undefined>();
  const id = objectiveId;
  const target = SubscriptionRequestTarget.OBJECTIVE;
  const types = [
    SubscriptionMessageType.OBJECTIVE,
    SubscriptionMessageType.TASK,
    SubscriptionMessageType.OBJECTIVE_ASSIGNMENT,
  ];
  if (!skip && authenticated && (lastId.current !== id || needReSubscription)) {
    handleSubUnSub(
      id,
      lastId.current,
      target,
      types,
      subscribe,
      unsubscribe,
      onJobUpdate
    );
    setNeedReSubscription(false);
    lastId.current = id;
  }
  const [objectiveCallback, setObjectiveCallback] = useState<
    ((u: ObjectiveUpdate) => void) | undefined
  >(() => onObjectiveUpdate);
  const [assignmentCallback, setAssignmentCallback] = useState<
    ((u: ObjectiveAssignmentUpdate) => void) | undefined
  >(() => onAssignmentUpdate);
  const [taskCallback, setTaskCallback] = useState<
    ((u: TaskUpdate) => void) | undefined
  >(() => onTaskUpdate);
  if (!authenticated && !needReSubscription) {
    setNeedReSubscription(true);
  }
  if (
    objectiveCallback !== onObjectiveUpdate ||
    assignmentCallback !== onAssignmentUpdate ||
    taskCallback !== onTaskUpdate
  ) {
    setNeedReSubscription(true);
    setObjectiveCallback(() => onObjectiveUpdate);
    setAssignmentCallback(() => onAssignmentUpdate);
    setTaskCallback(() => onTaskUpdate);
  }
};

export const useTaskSubscription = ({
  taskId,
  onTaskUpdate,
  skip = false,
}: {
  taskId: string;
  onTaskUpdate?: (resp: TaskUpdate) => void;
  skip?: boolean;
}): void => {
  const lastId = useRef<string | undefined>();
  const [needReSubscription, setNeedReSubscription] = useState<boolean>(false);
  const onJobUpdate = useCallback(
    (m: JobServiceSubscriptionMessage) => {
      if (m.task) {
        onTaskUpdate?.(m.task);
      }
    },
    [onTaskUpdate]
  );
  const { authenticated, subscribe, unsubscribe } = useJobWebsocket();
  const id = taskId;
  const target = SubscriptionRequestTarget.TASK;
  const types = [SubscriptionMessageType.TASK];
  if (!skip && authenticated && (lastId.current !== id || needReSubscription)) {
    setNeedReSubscription(false);
    handleSubUnSub(
      id,
      lastId.current,
      target,
      types,
      subscribe,
      unsubscribe,
      onJobUpdate
    );
    lastId.current = id;
  }
  if (!authenticated && !needReSubscription) {
    setNeedReSubscription(true);
  }
  const [taskCallback, setTaskCallback] = useState<
    ((t: TaskUpdate) => void) | undefined
  >(() => onTaskUpdate);
  if (taskCallback !== onTaskUpdate) {
    setNeedReSubscription(true);
    setTaskCallback(() => onTaskUpdate);
  }
};

export const useInterventionSubscription = ({
  onInterventionUpdate,
  skip = false,
}: {
  onInterventionUpdate?: (resp: InterventionUpdate) => void;
  skip?: boolean;
}): void => {
  // because this doesnt have an identifier to index off, we need to subscribe
  // by default.
  const [needReSubscription, setNeedReSubscription] = useState<boolean>(true);
  const onJobUpdate = useCallback(
    (m: JobServiceSubscriptionMessage) => {
      if (m.intervention) {
        onInterventionUpdate?.(m.intervention);
      }
    },
    [onInterventionUpdate]
  );
  const { authenticated, subscribe } = useJobWebsocket();

  const target = SubscriptionRequestTarget.INTERVENTIONS;
  if (!skip && authenticated && needReSubscription) {
    subscribe("", target, SubscriptionMessageType.INTERVENTION, onJobUpdate);
    setNeedReSubscription(false);
  }

  const [interventionCallback, setInterventionCallback] = useState<
    ((u: InterventionUpdate) => void) | undefined
  >(() => onInterventionUpdate);
  if (!authenticated && !needReSubscription) {
    setNeedReSubscription(true);
  }
  if (interventionCallback !== onInterventionUpdate) {
    setNeedReSubscription(true);
    setInterventionCallback(() => onInterventionUpdate);
  }
};

const handleSubUnSub = (
  id: string,
  oldId: string | undefined,
  target: SubscriptionRequestTarget,
  types: SubscriptionMessageType[],
  subscribe: (
    identifier: string,
    target: SubscriptionRequestTarget,
    type: SubscriptionMessageType,
    callback: (u: JobServiceSubscriptionMessage) => void
  ) => void,
  unsubscribe: (
    identifier: string,
    subscriptionType: SubscriptionRequestTarget,
    type: SubscriptionMessageType
  ) => void,
  onJobUpdate: (u: JobServiceSubscriptionMessage) => void
): void => {
  for (const type of types) {
    if (oldId && oldId !== id) {
      unsubscribe(oldId, target, type);
    }
    if (id) {
      subscribe(id, target, type, onJobUpdate);
    }
  }
};
